<template>
  <div class="fin-page">
      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#00AEEF" size="48" />
        <p>{{ loadingMessage }}</p>
      </div>

      <div v-else class="success-card">
        <div class="success-icon" :class="{ pending: issuancePending }">
          <v-icon size="48" color="white">{{ issuancePending ? 'mdi-clock-outline' : 'mdi-check-circle' }}</v-icon>
        </div>
        <h1>{{ headline }}</h1>
        <p class="sub">{{ completionSubtext }}</p>

        <div class="details-grid">
          <div v-if="docLookupPolicyNumber" class="detail-item">
            <label>Pacis policy</label>
            <p class="mono">{{ docLookupPolicyNumber }}</p>
          </div>
          <div class="detail-item">
            <label>Mapfre reference</label>
            <p>{{ displayMapfreNumber }}</p>
          </div>
          <div class="detail-item">
            <label>Quote reference</label>
            <p class="mono">{{ resolvedQuoteId }}</p>
          </div>
          <div v-if="quote.productLabel" class="detail-item">
            <label>Product</label>
            <p>{{ quote.productLabel }}</p>
          </div>
          <div v-if="quote.kshAmount" class="detail-item">
            <label>Premium paid</label>
            <p class="premium">KES {{ formatMoney(quote.kshAmount) }}</p>
          </div>
        </div>

        <policy-documents-panel
          v-if="docLookupPolicyNumber"
          :policy-number="docLookupPolicyNumber"
          :policy-document="policyDocument"
          :loading="docLoading"
          :error="docError"
          :loading-message="docLoadingMessage"
          :waiting-message="docWaitingMessage"
          compact
          @retry="checkPolicyDocument"
        />

        <p
          v-if="displayMapfreNumber && displayMapfreNumber !== '—' && displayMapfreNumber !== 'Pending…'"
          class="email-note"
        >
          A copy of your travel certificate is also emailed when the policy is issued.
        </p>

        <div class="actions">
          <button type="button" class="btn-outline" @click="$router.push({ name: travelConfig.routes.dashboard })">
            Dashboard
          </button>
          <button
            v-if="docLookupPolicyNumber"
            type="button"
            class="btn-outline"
            @click="checkPolicyDocument"
          >
            Refresh document
          </button>
          <button type="button" class="btn-primary" @click="startNewQuote">
            New travel quote
          </button>
        </div>
      </div>
    </div>
</template>

<script>
import PolicyDocumentsPanel from '@/components/pa/PolicyDocumentsPanel';
import agentTravelApi from '@/services/agentTravelApi';
import {
  clearTravelQuoteCache,
} from '@/utils/saleCache';
import { pollPolicyDocument, resolvePaPolicyDocument } from '@/utils/policyDocumentsApi';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { getTravelChannelConfig } from '@/utils/travelChannel';

const DOC_POLL_MS = 5000;
const DOC_MAX_ATTEMPTS = 24;
const ISSUANCE_POLL_MS = 5000;
const ISSUANCE_MAX_ATTEMPTS = 36;

export default {
  name: 'TravelFinalizationPage',
  components: { PolicyDocumentsPanel },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (v) => v === 'agent' || v === 'customer',
    },
    quoteId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: true,
      loadingMessage: 'Loading policy details…',
      quote: {},
      issuancePending: false,
      docLoading: false,
      docError: '',
      docLoadingMessage: 'Checking for policy document…',
      policyDocument: null,
      pollTimer: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    travelConfig() {
      return getTravelChannelConfig({ travelChannel: this.channel });
    },
    resolvedQuoteId() {
      return this.quoteId || this.$route.params.quoteId;
    },
    displayMapfreNumber() {
      const num = this.quote.policyNumber
        || this.quote.policyNo
        || this.$route.query.policyNo;
      return num || (this.issuancePending ? 'Pending…' : '—');
    },
    docLookupPolicyNumber() {
      return this.quote.tqPolicyNumber || this.quote.tq_policy_number || '';
    },
    headline() {
      return this.issuancePending ? 'Payment received' : 'Travel policy issued';
    },
    completionSubtext() {
      if (this.issuancePending) {
        return 'Your policy is being issued. The certificate will appear here once registered.';
      }
      if (this.policyDocument) {
        return 'Policy document is ready. A copy is also emailed to the customer.';
      }
      if (this.docLookupPolicyNumber) {
        return 'Policy issued. The certificate is being archived and will also be emailed.';
      }
      return 'Policy issued. The certificate is being prepared and will also be emailed.';
    },
    docWaitingMessage() {
      return 'Policy certificate is being archived in DocuWare. It is also sent by email when ready.';
    },
  },
  async mounted() {
    if (this.travelConfig.trackAnalytics) {
      trackAgentInteraction(this, 'travel_finalization', { quoteId: this.resolvedQuoteId });
    }
    await this.loadQuoteDetails();
  },
  beforeDestroy() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  methods: {
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    startNewQuote() {
      clearTravelQuoteCache(this.authenticatedUser?.email, this.resolvedQuoteId);
      this.$router.push({
        name: this.travelConfig.routes.planSelect,
        query: { journey: this.travelConfig.defaultJourney, reset: 'true' },
      });
    },
    mergeQuoteFromApi(data) {
      return {
        ...this.quote,
        ...data,
        policyNumber: data.policyNumber || data.policy_number || this.quote.policyNumber,
        tqPolicyNumber: data.tqPolicyNumber || data.tq_policy_number || this.quote.tqPolicyNumber,
      };
    },
    async loadQuoteDetails() {
      this.loading = true;
      const email = this.authenticatedUser?.email;
      clearTravelQuoteCache(email, this.resolvedQuoteId);
      let q = {};
      const res = await agentTravelApi.getQuote(this.resolvedQuoteId);
      if (res.success && res.data) {
        q = {
          ...res.data,
          resolvedQuoteId: this.resolvedQuoteId,
          productLabel: res.data.productLabel || res.data.plan_type,
          kshAmount: res.data.kshAmount || res.data.ksh_amount || this.$route.query.kshAmount,
          policyNumber: res.data.policyNumber || res.data.policy_number || this.$route.query.policyNo,
          tqPolicyNumber: res.data.tqPolicyNumber || res.data.tq_policy_number,
        };
      } else {
        q = {
          resolvedQuoteId: this.resolvedQuoteId,
          productLabel: this.$route.query.productLabel,
          kshAmount: this.$route.query.kshAmount,
          policyNumber: this.$route.query.policyNo,
        };
      }
      this.quote = q;
      this.loading = false;

      if (this.docLookupPolicyNumber) {
        await this.checkPolicyDocument(true);
        this.issuancePending = false;
        return;
      }
      this.issuancePending = true;
      this.pollForIssuance(1);
    },
    pollForIssuance(attempt) {
      if (attempt > ISSUANCE_MAX_ATTEMPTS) return;
      this.pollTimer = setTimeout(async () => {
        const res = await agentTravelApi.getQuote(this.resolvedQuoteId);
        if (res.success && res.data) {
          this.quote = this.mergeQuoteFromApi(res.data);
          if (this.quote.policyNumber) {
            this.issuancePending = false;
          }
          if (this.docLookupPolicyNumber) {
            await this.checkPolicyDocument(true);
            return;
          }
        }
        this.pollForIssuance(attempt + 1);
      }, ISSUANCE_POLL_MS);
    },
    async checkPolicyDocument(autoPoll = false) {
      const policyNumber = this.docLookupPolicyNumber;
      if (!policyNumber) return;

      this.docLoading = true;
      this.docError = '';
      this.docLoadingMessage = autoPoll
        ? 'Waiting for policy document…'
        : 'Checking for policy document…';

      try {
        if (autoPoll) {
          const result = await pollPolicyDocument(policyNumber, {
            intervalMs: DOC_POLL_MS,
            maxAttempts: DOC_MAX_ATTEMPTS,
            onAttempt: (attempt, max) => {
              this.docLoadingMessage = `Waiting for policy document… (${attempt}/${max})`;
            },
          });
          this.policyDocument = result.document;
        } else {
          const result = await resolvePaPolicyDocument(policyNumber);
          if (result.error) this.docError = result.error;
          this.policyDocument = result.document;
        }
      } finally {
        this.docLoading = false;
      }
    },
  },
  metaInfo: { title: 'Travel — Policy Issued' },
};
</script>

<style scoped>
.fin-page { max-width: 640px; margin: 0 auto; padding: 32px 24px; }
.loading-wrap { text-align: center; padding: 48px; color: #64748B; }
.success-card {
  background: white; border-radius: 16px; padding: 32px 28px;
  box-shadow: 0 4px 24px rgba(24,70,121,0.1); border: 1px solid #E8EDF2; text-align: center;
}
.success-icon {
  width: 72px; height: 72px; border-radius: 50%; background: #16A34A;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;
}
.success-icon.pending { background: #F59E0B; }
.success-card h1 { font-size: 1.4rem; color: #1E4276; margin: 0 0 8px; }
.sub { color: #64748B; font-size: 0.9rem; margin: 0 0 24px; }
.email-note {
  margin: 0 0 24px; padding: 14px 16px; border-radius: 10px;
  background: #F0F9FF; border: 1px solid #BAE6FD; color: #0C4A6E;
  font-size: 0.88rem; line-height: 1.5; text-align: left;
}
.details-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px; text-align: left;
  background: #F8FAFC; border-radius: 12px; padding: 20px; margin-bottom: 24px;
}
.detail-item label {
  font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em;
  color: #94A3B8; font-weight: 600;
}
.detail-item p { margin: 4px 0 0; font-weight: 600; color: #1E4276; font-size: 0.9rem; }
.detail-item .premium { color: #184679; font-size: 1.1rem; }
.detail-item .mono { font-family: monospace; font-size: 0.75rem; word-break: break-all; }
.actions { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
.btn-outline, .btn-primary {
  display: inline-flex; align-items: center; padding: 12px 20px; border-radius: 10px;
  font-weight: 700; cursor: pointer; border: none;
}
.btn-outline { background: white; border: 1px solid #CBD5E1; color: #1E4276; }
.btn-primary { background: #00AEEF; color: white; }
@media (max-width: 500px) {
  .fin-page { padding: 16px 12px 88px; }
  .success-card { padding: 22px 16px; border-radius: 14px; }
  .success-card h1 { font-size: 1.2rem; }
  .sub { font-size: 0.82rem; }
  .email-note { font-size: 0.8rem; padding: 12px; }
  .details-grid {
    grid-template-columns: 1fr;
    padding: 14px;
    gap: 12px;
  }
  .actions {
    flex-direction: column;
    align-items: stretch;
  }
  .actions .btn-outline,
  .actions .btn-primary {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }
}
</style>
